import { useState } from "react";
import { siteInfo } from "@/mocks/site";
import { testimonials } from "@/mocks/comics";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/d9oncfosenbniolesi90";

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      setSubmitting(false);
      return;
    }

    try {
      const submitAddr = FORM_SUBMIT_URL;
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "phone_alt")
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          "提交失败，请稍后重试";
        setFormError(serverMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
      setFormStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-20 pb-14">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "AboutPage",
              "name": "关于羞羞歪歪漫画",
              "description": siteInfo.description,
              "about": {
                "@type": "Organization",
                "name": "羞羞歪歪漫画",
                "foundingDate": siteInfo.founded,
                "description": siteInfo.description,
                "email": siteInfo.email
              }
            })
          }}
        />
        <Breadcrumb items={[{ label: "关于我们" }]} />

        {/* Page Header */}
        <section className="text-center mb-12">
          <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-3">
            关于羞羞歪歪漫画
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
            {siteInfo.description}
          </p>
        </section>

        {/* Brand Story */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-primary-500" />
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              品牌故事
            </h2>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              羞羞歪歪漫画成立于{siteInfo.founded}年，由一群热爱漫画的年轻人共同创立。我们深知漫画爱好者在寻找高质量阅读资源时的痛点——付费墙、广告干扰、内容删减、糟糕的阅读体验……正是这些困扰，让我们下定决心打造一个真正为读者着想的漫画平台。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              从最初的几百部漫画资源，到如今收录超过{siteInfo.stats[0].value}部热门作品，羞羞歪歪漫画始终秉持着"免费、完整、优质"的核心理念。我们坚持所有内容免费开放，坚持原版无删减，坚持为读者提供最舒适的竖屏下拉式阅读体验。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed">
              如今，羞羞歪歪漫画已拥有超过{siteInfo.stats[1].value}活跃用户，好评率高达{siteInfo.stats[2].value}。这些数字的背后，是每一位读者对我们的信任与支持。我们将继续努力，不断丰富漫画资源，优化阅读体验，让更多热爱漫画的朋友在这里找到属于自己的快乐。
            </p>
          </div>
        </section>

        {/* Core Features */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-accent-500" />
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              我们的优势
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {siteInfo.features.map((feature, idx) => (
              <div
                key={idx}
                className="p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className={`${feature.icon} text-lg text-primary-600`}></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="mb-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {siteInfo.stats.map((stat, idx) => (
              <div
                key={idx}
                className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
              >
                <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-accent-500" />
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              用户口碑
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="p-5 rounded-lg bg-background-50 border border-background-200/70"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">
                      {t.name}
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i
                          key={i}
                          className="ri-star-fill text-accent-500 text-xs"
                        ></i>
                      ))}
                    </div>
                  </div>
                  <span className="ml-auto text-xs text-foreground-400 whitespace-nowrap">
                    {t.date}
                  </span>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t.content}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-primary-500" />
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              联系我们
            </h2>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
            <p className="text-sm text-foreground-500 mb-6">
              如果您有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。我们会在24小时内回复您。
            </p>

            {formStatus === "success" ? (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-primary-50 border border-primary-200 text-primary-700">
                <i className="ri-checkbox-circle-line text-xl"></i>
                <span className="text-sm">
                  提交成功！感谢您的留言，我们会尽快回复。
                </span>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-form"
              >
                {formStatus === "error" && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-accent-50 border border-accent-200 text-accent-700 mb-4">
                    <i className="ri-error-warning-line text-xl"></i>
                    <span className="text-sm">{formError}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block text-sm text-foreground-700 mb-1.5"
                    >
                      姓名 *
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block text-sm text-foreground-700 mb-1.5"
                    >
                      邮箱 *
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="contact-subject"
                    className="block text-sm text-foreground-700 mb-1.5"
                  >
                    主题 *
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="contact-message"
                    className="block text-sm text-foreground-700 mb-1.5"
                  >
                    留言内容 *
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  />
                  <p className="text-xs text-foreground-400 mt-1">
                    最多500个字符
                  </p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      发送留言
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

        {/* Contact Info */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-accent-500" />
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              联系方式
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-lg text-primary-600"></i>
              </div>
              <div>
                <div className="text-xs text-foreground-500">电子邮箱</div>
                <div className="text-sm text-foreground-800 font-medium">
                  {siteInfo.email}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-lg text-accent-600"></i>
              </div>
              <div>
                <div className="text-xs text-foreground-500">工作时间</div>
                <div className="text-sm text-foreground-800 font-medium">
                  周一至周日 9:00-21:00
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-message-3-line text-lg text-secondary-600"></i>
              </div>
              <div>
                <div className="text-xs text-foreground-500">响应时间</div>
                <div className="text-sm text-foreground-800 font-medium">
                  24小时内回复
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}